import React from 'react';

import {
    Theme,
    ThemeCSSVariables,
    ThemeContext as ThemeContextType,
} from './types';

import autumnThemeConfig from './configs/autumn';
import darkThemeConfig from './configs/dark';
import lightThemeConfig from './configs/light';
import springThemeConfig from './configs/spring';
import { LS_KEY, NOT_ACTIVE_THEMES, THEMES } from './constants';

const value: ThemeContextType = {
    theme: 'light',
    setTheme: (theme) => theme,
    toggleTheme: () => 'light',
};

const ThemeContext = React.createContext<ThemeContextType>(value);

const classNameMap: Record<Theme, string> = {
    light: 'vchasno-light-theme',
    dark: 'vchasno-dark-theme',
    autumn: 'vchasno-autumn-theme',
    spring: 'vchasno-spring-theme',
};

const cssConfigMap: Record<Theme, Partial<ThemeCSSVariables>> = {
    light: lightThemeConfig,
    dark: darkThemeConfig,
    autumn: autumnThemeConfig,
    spring: springThemeConfig,
};

interface ThemeContextProviderProps {
    children: React.ReactNode;
    rootNode?: HTMLElement;
}

const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({
    children,
    rootNode,
}) => {
    const rootRef = React.useRef<HTMLElement>(
        rootNode || document.documentElement,
    );
    const [theme, setTheme] = React.useState<ThemeContextType['theme']>(() => {
        const themeFromLS = localStorage.getItem(
            LS_KEY,
        ) as ThemeContextType['theme'];
        return THEMES.includes(themeFromLS) ? themeFromLS : 'light';
    });

    const setCSSVariables = (themeConfig: Partial<ThemeCSSVariables>) => {
        rootRef.current.removeAttribute('style');
        Object.entries(themeConfig).forEach(([ccsKey, cssValue]) => {
            rootRef.current.style.setProperty(ccsKey, cssValue as string);
        });
    };

    const setRootThemeClassName = (nextTheme: ThemeContextType['theme']) => {
        Object.values(classNameMap).forEach((className) => {
            rootRef.current.classList.remove(className);
        });

        rootRef.current.classList.add(classNameMap[nextTheme]);
    };

    const applyTheme = (nextTheme: ThemeContextType['theme']) => {
        setCSSVariables(cssConfigMap[nextTheme]);
        setRootThemeClassName(nextTheme);
    };

    React.useLayoutEffect(() => {
        applyTheme(theme);
        localStorage.setItem(LS_KEY, theme);
    }, [theme]);

    const toggleTheme = () => {
        let nextThemeIndex = THEMES.indexOf(theme) + 1;
        let nextTheme = THEMES[nextThemeIndex % THEMES.length];

        if (NOT_ACTIVE_THEMES.includes(nextTheme)) {
            nextThemeIndex = (nextThemeIndex + 1) % THEMES.length;
            nextTheme = THEMES[nextThemeIndex];
        }

        setTheme(nextTheme);
        return nextTheme;
    };

    return (
        <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useThemeContext = () => React.useContext(ThemeContext);

export default ThemeContextProvider;
